/* @flow */
import _, { cloneDeep } from 'lodash';

import Activity from 'entities/Activity';
import apiRequest from 'lib/apiRequest';
import ChatMembership from 'entities/ChatMembership';
import ChatMemberships from 'collections/ChatMemberships';
import ChatMessage from 'entities/ChatMessage';
import Entity from 'lib/Entity';

import type { PotentialOrRegularChatMember } from 'entities/PotentialChatMember';

export const TYPES = {
  ACTIVITY: 'activity',
  GROUP: 'group',
  INDIVIDUAL: 'individual',
  ASSISTANT: 'assistant',
};

export type ChatTypeType = 'activity' | 'group' | 'individual' | 'assistant';
export const MAX_PARTICIPANTS_REPLIES_ENABLE = 200;

export type OtherMemberType = {
  id: number,
  initials: string,
  color: string, // 6-char hex code
  name: string,
};

class Chat extends Entity {
  initialize() {
    this.idAttribute = 'uuid';
    this.urlRoot = '/chats';
  }

  defaults(): {group_summaries: Array<Object>, memberships: Array<ChatMembership>} {
    // typecheck: ignore statement
    return {
      active: true,
      can_close: true,
      can_open: false,
      group_summaries: [],
      memberships: [],
      last_read_seq: null,
      present: true,
      state: 'open',
      can_send: true,
      updated_at: new Date(),
    };
  }

  parse(response: Object) {
    const chat = cloneDeep(response);

    if (chat['last_message']) {
      chat['last_message'] = ChatMessage.fromJSON(chat['last_message']);
    }

    if (chat['activity']) {
      chat['activity'] = Activity.fromJSON(chat['activity']);
    }

    chat['memberships'] = ChatMemberships.fromJSON(chat['memberships']);
    chat['updated_at'] = new Date(chat['updated_at']);
    return chat;
  }

  isActive(): boolean {
    return !!this.get('active');
  }

  hasTitle(): boolean {
    return this.get('title').length > 0;
  }

  getTitle(): string {
    return this.get('title');
  }

  getQueryKey() {
    return this.get('query_key');
  }

  canAdd(): boolean {
    return !!this.get('can_add');
  }

  canManageReplies(): boolean {
    return !!this.get('can_manage_replies');
  }

  canOpen(): boolean {
    return !!this.get('can_open');
  }

  canLeave(): boolean {
    return !!this.get('can_leave');
  }

  canSend(): boolean {
    return !!this.get('can_send');
  }

  hasRepliesDisabled(): boolean {
    return this.getState() === 'replies_disabled';
  }

  isInGroup(groupId: number) {
    return !!this.get('group_summaries').find(group => group.id === groupId);
  }

  isRemoved(): boolean {
    return this.getState() === 'removed';
  }

  isDead(): boolean {
    return this.getState() === 'dead';
  }

  hasLeft(): boolean {
    return this.getState() === 'left';
  }

  isOpen(): boolean {
    return this.getState() === 'open';
  }

  getMaximumMembers(): number {
    return this.get('maximum_members');
  }

  hasReachedParticipantNumberLimit(): boolean {
    return this.getPresentMemberships().length >= this.getMaximumMembers();
  }

  // NOTE: Not to be confused with the !this.isActive()
  isInactive(): boolean {
    return this.hasLeft() || this.isRemoved() || this.isDead();
  }

  getCreatorId() {
    return this.get('creator_id');
  }

  getActivity() {
    return this.get('activity');
  }

  getType(): ?ChatTypeType {
    return this.get('type');
  }

  isActivityType(): boolean {
    return this.getType() === TYPES.ACTIVITY;
  }

  isAssistantType(): boolean {
    return this.getType() === TYPES.ASSISTANT;
  }

  isGroupType(): boolean {
    return this.getType() === TYPES.GROUP;
  }

  isIndividualType(): boolean {
    return this.getType() === TYPES.INDIVIDUAL;
  }

  getLastMessage(): ?ChatMessage {
    return this.get('last_message');
  }

  // TODO: Get rid of this (immutable data)
  setLastMessage(message: ChatMessage) {
    return this.set({ last_message: message });
  }

  // legacy state
  isPaused(): boolean {
    return this.getState() === 'paused';
  }

  // legacy state
  isClosed(): boolean {
    return this.getState() === 'closed';
  }

  // We still received pusher update on groups
  // with the old states, this helper handles it.
  isDisabled(): boolean {
    return (
      this.isPaused() ||
      this.hasRepliesDisabled() ||
      this.isClosed()
    );
  }

  hasMutedMembers(): boolean {
    return !!this.get('has_muted_members');
  }

  getGroupSummaries() {
    return this.get('group_summaries');
  }

  getAssociatedGroupUuids() {
    return this.getGroupSummaries().map(summary => summary.uuid);
  }

  // HACK param because of circular dependencies
  markAsReadForAssociatedGroups(unreadStore: any) {
    const unreadCount = this.getUnreadMessages();
    if (!unreadCount) {
      return;
    }

    const groupUuids = this.getAssociatedGroupUuids();
    groupUuids.forEach(uuid => unreadStore.decrementUnreads(uuid, unreadCount));
  }

  getLastReadSeq(): number {
    return this.get('last_read_seq');
  }

  // Note: may not be loaded if the chat is loaded by v2/chats
  // This should return ChatMemberships but with ChatMembershipsMock it returns ChatMembership[]
  getMemberships() {
    return this.get('memberships');
  }

  // Note: may not be loaded if the chat is loaded by v2/chats
  getMemberIds(): number[] {
    return this.getMemberships().map(membership => membership.getMember().id);
  }

  // Chats now have origin_ids which are the groups the chat should be attached to
  // (For example, what group was the user in when they created the chat?)
  getOriginIds() {
    return this.get('origin_ids');
  }

  isPresent(): boolean {
    return this.get('present');
  }

  getLeftMemberships(): ChatMembership[] {
    return this.getMemberships().filter(membership => membership.getState() === 'left');
  }

  getPresentMemberships(): ChatMembership[] {
    return this.getMemberships().filter(membership => membership.getState() === 'present');
  }

  getOtherPresentMemberships(currentUserId: ?number): ChatMembership[] {
    return this.getMemberships().filter(membership => membership.getState() === 'present'
      && membership.getMember().id !== currentUserId);
  }

  getOtherChatMemberships(allowEmpty: boolean = false) {
    // XXX remove CurrentUser from entities
    const CurrentUser = require('auth/CurrentUser').default;
    const currentUserId = CurrentUser.getUserId();
    const memberships = new ChatMemberships(this.getMemberships().filter(membership => {
      return membership.getMember().id !== currentUserId;
    }));

    // Note: this is a temporary fix until the backend fixes issues with deleted chat memberships
    return (memberships.length > 0) || allowEmpty ? memberships : this.getMemberships();
  }

  getOtherChatMembers(allowEmpty: boolean = false): PotentialOrRegularChatMember[] {
    return this.getOtherChatMemberships(allowEmpty).map(membership => {
      return membership.getMember();
    });
  }

  getOwnerChatMembership(): ?ChatMembership {
    return this.getMemberships().filter(membership => {
      return membership.getMember().id === this.getCreatorId();
    })[0];
  }

  getOtherChatMembershipsWithState(state: string) {
    return this.getOtherChatMemberships().reduce((results, membership) => {
      if (!state || (membership.getState() === state)) {
        results.push(membership);
      }
      return results;
    }, []);
  }

  getMembership(member: PotentialOrRegularChatMember): ?ChatMembership {
    const memberships = this.getMemberships().filter(membership => {
      return membership.getMember().id === member.id;
    });

    return memberships[0];
  }

  isPresentMember(member: PotentialOrRegularChatMember): boolean {
    const membership = this.getMembership(member);
    return !!membership && membership.isPresent();
  }

  createNewMembership(member: PotentialOrRegularChatMember): void {
    this.getMemberships().add(ChatMembership.create(member));
  }

  addToMemberships(membership: ChatMembership): void {
    this.getMemberships().add(membership);
  }

  removeFromMemberships(membership: ChatMembership): void {
    this.getMemberships().remove(membership);
  }

  /* DEPRECATED, use isGroupType */
  isGroupChat(): boolean {
    return this.getMemberships().length > 2;
  }

  getState(): string {
    return this.get('state');
  }

  getColor(): ?string {
    return this.get('color');
  }

  getUnreadMessages() {
    return this.get('unread_messages');
  }

  getUpdatedAt(): Date {
    return this.get('updated_at');
  }

  // TODO: Get rid of this (immutable data)
  setUpdatedAt(date: Date): void {
    this.set({ updated_at: date });
  }

  isUnsubscribed(): boolean {
    return this.get('state') === 'dead';
  }

  // TODO: Get rid of this? (immutable data)
  markAsRead(): void {
    this.set({ unread_messages: 0 });
    const lastMessage = this.getLastMessage();
    if (lastMessage) {
      this.set({ last_read_seq: lastMessage.getSeq() });
    }
  }

  deactivate(): void {
    this.set({ active: false });
  }

  leave() {
    return this.save({
      active: this.getMemberships().where({ state: 'present' }).length - 1 > 1,
      can_open: true,
      present: false,
    }, {
      data: { chat: { present: false } },
      patch: true,
    });
  }

  initiate(message: ChatMessage) {
    return apiRequest('/chats', 'POST', {
      chat: {
        origin_ids: this.getOriginIds(),
        member_ids: this.getMemberIds(),
        message: {
          body: message.getBody(),
          file: message.getFile(),
          uuid: message.getUuid(),
        },
      },
    }).then(chat => Chat.fromJSON(chat));
  }

  isOutsideOfOtherOfficeHours(): boolean {
    return this.getOtherChatMembers().some(member => {
      const officeHours = member.getOfficeHours();
      if (officeHours && !officeHours.isAvailableNow()) {
        return true;
      }
      return false;
    });
  }

  getOtherMember(): ?OtherMemberType {
    return this.get('other_member');
  }

  getSortKey(): string {
    return this.get('sort_key');
  }

  // Extract the timestamp from sort_key. If it fails, fall back to updated_at
  getSortKeyTimestamp(): Date {
    try {
      const parsedDate = new Date(this.getSortKey().split('UTC')[0] + 'UTC');
      if (isNaN(parsedDate.getTime())) { // Detect 'Invalid Date'
        return this.getUpdatedAt();
      }
      return parsedDate;
    } catch (e) {} // eslint-disable-line
    return this.getUpdatedAt();
  }

  addMembers(membersIds: Array<number>): Promise<ChatMembership[]> {
    return apiRequest(`/chats/${this.id}/members`, 'POST', {
      member_ids: membersIds,
    }).then(memberships => {
      return memberships.map((m) => ChatMembership.fromJSON(m));
    });
  }

  changeMemberStatus(membersId: number, state: string): Promise<ChatMembership> {
    return apiRequest(`/chats/${this.id}/members/${membersId}`, 'PATCH', {
      member: { state },
    }).then(membership => {
      return ChatMembership.fromJSON(membership);
    });
  }

  disableReplies(): Promise<Chat> {
    return apiRequest(`/chats/${this.id}`, 'PATCH', {
      chat: { state: 'replies_disabled' },
    }).then(chat => Chat.fromJSON(chat));
  }

  enableReplies(): Promise<Chat> {
    return apiRequest(`/chats/${this.id}`, 'PATCH', {
      chat: { state: 'open' },
    }).then(chat => Chat.fromJSON(chat));
  }

  updateTitle(title: string): Promise<void> {
    return this.save({
      chat: { title },
    }, {
      patch: true,
    });
  }

  // memoize so this calculation doesn't create a new object
  // and bust the shallow compare.
  getDisplayNames: Function = _.memoize(() => {
    const present = this.getOtherChatMembershipsWithState('present');
    let names = present.map(membership => {
      return membership.getMember().getName();
    });

    if (names.length === 0) {
      const ownerMembership = this.getOwnerChatMembership();
      if (ownerMembership) {
        names = [ownerMembership.getMember().getName()];
      }
    }

    return names;
  });

  static createLocal(members, title, originIds = []): Chat {
    const memberships = new ChatMemberships(members.map(member => ChatMembership.create(member)));
    const otherChatMember = memberships.first().getMember();

    return new Chat({
      memberships,
      other_member: {
        color: otherChatMember.getColor() || '',
        id: -1,
        initials: otherChatMember.getInitials() || '',
        name: '',
      },
      origin_ids: originIds,
      title,
      type: members.length === 2 ? TYPES.INDIVIDUAL : TYPES.GROUP,
    });
  }

  static markReadUpto(chatId: string, seq: number): Promise<void> {
    return apiRequest(`/chats/${chatId}`, 'PATCH', {
      chat: {
        last_read_seq: seq,
      },
    });
  }

  static emailTranscript(chatId: string): Promise<void> {
    return apiRequest(`/chats/${chatId}/email_transcript`, 'POST', {});
  }

  static reportChat(chatId: string, uuid: string, reason: string, notes: ?string): Promise<void> {
    const reasonAndNotes = notes ? `${reason} - ${notes}` : reason;
    return apiRequest(`/chats/${chatId}/report`, 'POST', {
      uuid: chatId,
      chat: {
        reason: reasonAndNotes,
        reported_user_uuid: uuid,
      },
    });
  }
}

export default Chat;



// WEBPACK FOOTER //
// ./src/entities/Chat.js